import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Notice, NoticeList, NoticeText } from "components/ui/Notice/Notice";
import { PageHeader } from "components/ui/PageHeader";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { useMergePatient } from "features/patients/hooks/useMergePatient";
import { usePatientData } from "features/patients/hooks/usePatientData";
import {
  getIdentifierConflicts,
  getMergePlan,
} from "features/patients/utils/patientMerge";
import { pathGenerators } from "utils/routes";

import { MergeRecordCard } from "./components/MergeRecordCard";
import { MergeResultSummary } from "./components/MergeResultSummary";
import styles from "./MergePatientsPage.module.css";

const SUBMITTED_MESSAGE_NOTE =
  "Submitted-message history stays with the original record. What was sent to the HSE was sent under that identity, so vaccination, CDM and referral submissions do not follow the patient.";

const patientName = (patient) =>
  [patient?.firstName, patient?.surname].filter(Boolean).join(" ") ||
  "this patient";

// Merging lives in the admin area rather than on a patient's chart: it moves clinical
// data between two records and is a practice-administration decision, not something to
// reach for mid-consultation. The screen sits under SettingsLayout, which already
// refuses anyone without ADMIN — the backend enforces it too (@Secured({"ADMIN"})),
// but the UI should not offer what a user cannot do.
const MergePatientsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [firstSelection, setFirstSelection] = useState(null);
  const [secondSelection, setSecondSelection] = useState(null);
  const [survivorId, setSurvivorId] = useState(null);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [completedMerge, setCompletedMerge] = useState(null);

  const mergePatient = useMergePatient();

  // PatientAutocomplete hands back {id, dateOfBirth, title} only. Everything the admin
  // needs to tell the two records apart — identifiers, contact details, address —
  // comes from each patient's own record.
  const { data: firstPatient } = usePatientData(firstSelection?.id);
  const { data: secondPatient } = usePatientData(secondSelection?.id);

  const plan = getMergePlan({ firstPatient, secondPatient, survivorId });
  const identifierConflicts = getIdentifierConflicts(
    firstPatient,
    secondPatient
  );

  // Choosing a different patient invalidates an acknowledgement of a pairing that no
  // longer applies, and a survivor chosen from a record no longer on screen.
  const handleFirstSelection = (selection) => {
    setFirstSelection(selection);
    setSurvivorId(null);
    setIsAcknowledged(false);
  };

  const handleSecondSelection = (selection) => {
    setSecondSelection(selection);
    setSurvivorId(null);
    setIsAcknowledged(false);
  };

  const handleChooseSurvivor = (patientId) => {
    setSurvivorId(patientId);
    setIsAcknowledged(false);
  };

  const handleMerge = () => {
    if (!plan.isReady) return;

    mergePatient.mutate(plan.request, {
      onSuccess: (result) => {
        // The two records are frozen into the result before the selections are
        // cleared: once the merge has happened, re-reading the merged-away patient
        // 404s and usePatientData navigates away from a page still showing its outcome.
        setCompletedMerge({
          survivor: plan.survivor,
          mergedAway: plan.mergedAway,
          recordsUpdated: result?.recordsUpdated,
        });
        setFirstSelection(null);
        setSecondSelection(null);
        setSurvivorId(null);
        setIsAcknowledged(false);
        queryClient.invalidateQueries();
      },
    });
  };

  const handleStartAnother = () => {
    setCompletedMerge(null);
  };

  if (completedMerge) {
    return (
      <div className={styles.page}>
        <PageHeader title="Merge complete" />
        <p className={styles.lead}>
          {patientName(completedMerge.mergedAway)}&apos;s records were merged into{" "}
          <strong>{patientName(completedMerge.survivor)}</strong>, and the merged
          record is no longer available.
        </p>
        <MergeResultSummary recordsUpdated={completedMerge.recordsUpdated} />
        <Notice tone="info">
          <NoticeText>{SUBMITTED_MESSAGE_NOTE}</NoticeText>
        </Notice>
        <div className={styles.actions}>
          <Button
            variant="outline"
            onClick={() =>
              navigate(pathGenerators.patient.details(completedMerge.survivor.id))
            }
          >
            Open the record you kept
          </Button>
          <Button onClick={handleStartAnother}>Merge another pair</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Merge patient records" />
      <p className={styles.lead}>
        Pick the two records, then choose which one is kept. Every appointment,
        consultation, document, immunisation and payment on the other record is moved
        onto the one you keep, and that record is then marked as merged.
      </p>

      <div className={styles.pickers}>
        <div className={styles.picker}>
          <label className={styles.pickerLabel} htmlFor="merge-patient-first">
            First record
          </label>
          <PatientAutocomplete
            id="merge-patient-first"
            value={firstSelection}
            onChange={handleFirstSelection}
            placeholder="Search by name..."
          />
        </div>
        <div className={styles.picker}>
          <label className={styles.pickerLabel} htmlFor="merge-patient-second">
            Second record
          </label>
          <PatientAutocomplete
            id="merge-patient-second"
            value={secondSelection}
            onChange={handleSecondSelection}
            placeholder="Search by name..."
          />
        </div>
      </div>

      {!!firstPatient && !!secondPatient && (
        <>
          <div className={styles.recordCards}>
            <MergeRecordCard
              patient={firstPatient}
              isSurvivor={survivorId === firstPatient.id}
              onChooseSurvivor={handleChooseSurvivor}
              isChoiceDisabled={mergePatient.isPending}
            />
            <MergeRecordCard
              patient={secondPatient}
              isSurvivor={survivorId === secondPatient.id}
              onChooseSurvivor={handleChooseSurvivor}
              isChoiceDisabled={mergePatient.isPending}
            />
          </div>

          {identifierConflicts.length > 0 && (
            <Notice tone="blocking" testId="merge-identifier-conflict">
              <NoticeText isLead>
                These records carry different national identifiers. That is evidence
                they are two different people. You can still merge them.
              </NoticeText>
              <NoticeList>
                {identifierConflicts.map((conflict) => (
                  <li key={conflict.label}>
                    {conflict.label}: {conflict.values[0]} and {conflict.values[1]}
                  </li>
                ))}
              </NoticeList>
            </Notice>
          )}

          <Notice tone="info">
            <NoticeText>{SUBMITTED_MESSAGE_NOTE}</NoticeText>
            <NoticeText>
              What moves is reported once the merge has run — it cannot be listed in
              advance.
            </NoticeText>
          </Notice>

          {!!plan.blocker && (
            <p className={styles.blocker}>{plan.blocker}</p>
          )}

          {plan.isReady && (
            <label className={styles.acknowledge}>
              <Checkbox
                checked={isAcknowledged}
                onChange={(e) => setIsAcknowledged(e.target.checked)}
                disabled={mergePatient.isPending}
              />
              <span>
                I understand that every record on{" "}
                <strong>{patientName(plan.mergedAway)}</strong> will be moved onto{" "}
                <strong>{patientName(plan.survivor)}</strong>, and that{" "}
                {patientName(plan.mergedAway)}&apos;s record will no longer be
                available.
              </span>
            </label>
          )}

          <div className={styles.actions}>
            <Button
              onClick={handleMerge}
              disabled={
                !plan.isReady || !isAcknowledged || mergePatient.isPending
              }
            >
              {mergePatient.isPending ? "Merging..." : "Merge records"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export { MergePatientsPage };
